import React from 'react';
import { motion } from 'framer-motion';
import { DocumentTextIcon, ScaleIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const Terms: React.FC = () => {
  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: DocumentTextIcon,
      content: [
        'By accessing and using AICaFriend, you accept and agree to be bound by these Terms of Service',
        'If you do not agree to these terms, please do not use our service',
        'These terms apply to all users, including CA firms, individual accountants, and their clients',
        'Continued use of the service constitutes acceptance of any updates to these terms'
      ]
    },
    {
      id: 'service-description',
      title: 'Service Description',
      icon: DocumentTextIcon,
      content: [
        'AICaFriend provides AI-powered invoice processing and data extraction services',
        'Our platform automates invoice digitization, data extraction, and workflow management',
        'We offer integrations with popular accounting software and cloud storage solutions',
        'Service features may vary based on your subscription plan and regional availability'
      ]
    },
    {
      id: 'user-responsibilities',
      title: 'User Responsibilities',
      icon: ShieldCheckIcon,
      content: [
        'Provide accurate account information and keep it updated',
        'Maintain the confidentiality of your account credentials',
        'Use the service only for legitimate business purposes',
        'Ensure you have rights to upload and process invoices through our platform',
        'Comply with all applicable laws and regulations in your jurisdiction'
      ]
    },
    {
      id: 'prohibited-uses',
      title: 'Prohibited Uses',
      icon: ExclamationTriangleIcon,
      content: [
        'Upload or process fraudulent, illegal, or unauthorized documents',
        'Attempt to reverse engineer, hack, or compromise our systems',
        'Share your account access with unauthorized parties',
        'Use the service to violate privacy rights or data protection laws',
        'Engage in any activity that could harm or disrupt our service'
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <ScaleIcon className="w-16 h-16 text-indigo-600 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Terms of Service</h1>
            <p className="text-xl text-gray-600">
              Please read these terms carefully before using AICaFriend. They govern your use of our platform and services.
            </p>
            <p className="text-sm text-gray-500 mt-4">Last updated: January 15, 2025</p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Welcome to AICaFriend</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms of Service ("Terms") constitute a legally binding agreement between you and AICaFriend 
              Technologies ("Company," "we," "us," or "our") regarding your use of the AICaFriend platform and services. 
              These Terms govern your access to and use of our AI-powered invoice processing platform.
            </p>
          </motion.div>

          {/* Main Sections */}
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                className="bg-gray-50 rounded-xl p-8"
              >
                <div className="flex items-center mb-4">
                  <section.icon className="w-8 h-8 text-indigo-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Payment Terms */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 bg-blue-50 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Payment Terms</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Subscription Fees</h4>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Monthly or annual billing cycles available</li>
                  <li>• Fees are charged in advance for each billing period</li>
                  <li>• No refunds for partial months or unused services</li>
                  <li>• Prices may change with 30 days advance notice</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Payment Processing</h4>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Secure payment processing via trusted providers</li>
                  <li>• Automatic renewal unless cancelled</li>
                  <li>• Failed payments may result in service suspension</li>
                  <li>• Taxes calculated based on your location</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Service Availability */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 bg-yellow-50 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Availability & Support</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Uptime & Availability</h4>
                <p className="text-gray-600 text-sm mb-2">
                  We strive to maintain 99.9% uptime but do not guarantee uninterrupted service availability.
                </p>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Scheduled maintenance windows communicated in advance</li>
                  <li>• Service status updates available on our status page</li>
                  <li>• Emergency maintenance may occur without notice</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Customer Support</h4>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• Email support for all subscription plans</li>
                  <li>• Priority support for Enterprise customers</li>
                  <li>• Support hours: Monday-Friday, 9 AM - 6 PM IST</li>
                  <li>• Emergency support available for critical issues</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Data & Privacy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 bg-green-50 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Ownership & Privacy</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Your Data Ownership</h4>
                <p className="text-gray-600 text-sm">
                  You retain all rights, title, and interest in your data. We process your data solely to provide 
                  our services and do not claim ownership of your content.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Data Processing</h4>
                <p className="text-gray-600 text-sm">
                  We process your data in accordance with our Privacy Policy and applicable data protection laws. 
                  Our AI models may use anonymized data to improve service quality.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Data Retention</h4>
                <p className="text-gray-600 text-sm">
                  Data is retained as per our Privacy Policy and your subscription terms. You may request data 
                  deletion upon account termination.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Limitation of Liability */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-12 bg-red-50 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Limitation of Liability</h3>
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                <strong>IMPORTANT:</strong> Please read this section carefully as it limits our liability to you.
              </p>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Service Accuracy</h4>
                  <p className="text-gray-600 text-sm">
                    While we strive for high accuracy, AI processing may contain errors. You are responsible for 
                    reviewing and validating all extracted data.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Maximum Liability</h4>
                  <p className="text-gray-600 text-sm">
                    Our total liability to you shall not exceed the amount paid by you for the service in the 
                    12 months preceding the claim.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Excluded Damages</h4>
                  <p className="text-gray-600 text-sm">
                    We shall not be liable for indirect, incidental, special, consequential, or punitive damages, 
                    including lost profits or data.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Termination */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-12"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Termination</h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">By You</h4>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    <li>• Cancel anytime from your account settings</li>
                    <li>• Service continues until end of billing period</li>
                    <li>• Data export available for 30 days post-cancellation</li>
                    <li>• No refunds for unused portions</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">By Us</h4>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    <li>• For material breach of these Terms</li>
                    <li>• For non-payment after reasonable notice</li>
                    <li>• For illegal or harmful use of our service</li>
                    <li>• 30 days notice for convenience termination</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Governing Law */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="mt-12 bg-indigo-50 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Governing Law & Disputes</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Jurisdiction</h4>
                <p className="text-gray-600 text-sm">
                  These Terms are governed by the laws of India. Any disputes shall be resolved in the courts of 
                  Bangalore, Karnataka, India.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Dispute Resolution</h4>
                <p className="text-gray-600 text-sm">
                  We encourage resolving disputes through direct communication. For formal disputes, we prefer 
                  arbitration under the Indian Arbitration and Conciliation Act, 2015.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact & Updates */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white"
          >
            <h3 className="text-xl font-semibold mb-4">Contact & Updates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Questions about Terms</h4>
                <p className="text-indigo-100 text-sm">legal@aicafriend.com</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Terms Updates</h4>
                <p className="text-indigo-100 text-sm">
                  We may update these terms periodically. Significant changes will be communicated via email.
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-indigo-400">
              <p className="text-sm text-indigo-100">
                By continuing to use AICaFriend after any updates to these Terms, you agree to be bound by the revised terms.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Terms;